import React, { useEffect, useMemo, FC, ReactNode } from 'react';
import { HttpTransportType, HubConnectionBuilder, HubConnection, HubConnectionState } from '@microsoft/signalr';

import Settings from "./api/Settings";

import NotificationContext from './NotificationContext';
import UserData from './classes/api/result/accountmodule/UserData';

interface NotificationContextProviderProps {
    currentUserData: UserData;
    children: ReactNode;
}

const NotificationContextProvider: FC<NotificationContextProviderProps> = ({ currentUserData, children }) => {
    const notificationConnection: HubConnection = useMemo(() => {
        return new HubConnectionBuilder()
            .withUrl(Settings.api_url + "/clientHub", { skipNegotiation: true, transport: HttpTransportType.WebSockets })
            .withAutomaticReconnect()
            .build();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            try {
                if (notificationConnection.state === HubConnectionState.Disconnected) {
                    await notificationConnection.start();
                }

                await notificationConnection.send("Register", currentUserData.UserId);
            } catch (err) {
                console.error("[SignalR] Connection error:", err);
                if (!cancelled) setTimeout(start, 3000);
            }
        };

        notificationConnection.onreconnected(async () => {
            try {
                await notificationConnection.send("Register", currentUserData.UserId);
            } catch (e) {
                console.error("[SignalR] Re-register error:", e);
            }
        });

        start();

        return () => {
            cancelled = true;
            notificationConnection
                .stop()
                .then(() => console.log("[SignalR] Stopped"))
                .catch((e) => console.error("[SignalR] Stop error:", e));
        };
    }, [notificationConnection, currentUserData.UserId]);

    return (
        <NotificationContext.Provider value={{ notificationConnection }}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContextProvider;
